import { useCanAccess, useGetList, useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";

import { panelClass } from "../palmtree/shell";

// Owner-only. Parker (A3) wants an approved item on its linked dossier and not
// on this screen. A2 still shows the block; Dudley chooses which one stays.
// Buyers never get here: canAccess denies them the sales list, and the view's
// RLS would hide the rows anyway.

type NewsFeedItem = {
  id: string;
  title: string;
  summary: string;
  label: string;
  source_name: string;
  source_url?: string | null;
  published_on: string;
  needs_confirmation?: boolean;
};

function sourceHref(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.href;
  } catch {
    return null;
  }
}

function formatNewsDate(publishedOn: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(publishedOn);
  if (!match) return publishedOn;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export const DashboardNews = () => {
  const translate = useTranslate();
  const access = useCanAccess({ resource: "sales", action: "list" });
  const news = useGetList<NewsFeedItem>(
    "market_news_feed",
    {
      pagination: { page: 1, perPage: 8 },
      sort: { field: "published_on", order: "DESC" },
      filter: {},
    },
    { enabled: access.canAccess === true },
  );

  if (access.isPending || access.canAccess !== true) return null;

  return (
    <section className={`${panelClass} lg:col-span-2`}>
      <h2 className="font-heading px-6 py-6 text-xl font-extrabold tracking-tight">
        {translate("palmtree.dashboard.news")}
      </h2>
      {news.isPending ? null : news.error ? (
        <p className="px-6 pb-6 text-[12.5px] text-muted-foreground">
          {translate("palmtree.dashboard.news_error")}
        </p>
      ) : !news.data?.length ? (
        <p className="px-6 pb-6 text-[12.5px] text-muted-foreground">
          {translate("palmtree.dashboard.news_empty")}
        </p>
      ) : (
        <ul className="flex flex-col px-6 pb-6">
          {news.data.map((item) => {
            const href = sourceHref(item.source_url);
            return (
              <li
                key={item.id}
                className="border-t border-border py-4 first:border-t-0 first:pt-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="chip">{item.label}</Badge>
                  {item.needs_confirmation ? (
                    <Badge variant="warn">
                      {translate("palmtree.dashboard.news_confirm")}
                    </Badge>
                  ) : null}
                  <span className="text-[12.5px] text-muted-foreground">
                    {item.source_name} · {formatNewsDate(item.published_on)}
                  </span>
                </div>
                <p className="mt-2 text-sm font-semibold">{item.title}</p>
                <p className="mt-1 text-[12.5px] text-muted-foreground">
                  {item.summary}
                </p>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-[13px] font-semibold text-link hover:underline"
                  >
                    {translate("palmtree.dashboard.open_source")}
                  </a>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
